import { isTokenPersistenceError } from 'auth/sessionClassification';
import { ApiError } from 'core/errors/ApiError';

export function mapFieldErrors(errors = {}) {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return {};
  }

  return Object.entries(errors).reduce((mapped, [path, messages]) => {
    const list = Array.isArray(messages) ? messages.filter(Boolean) : [messages].filter(Boolean);
    if (list.length > 0) {
      mapped[path] = list.map(String);
    }
    return mapped;
  }, {});
}

export function firstFieldError(errors = {}, path) {
  const mapped = mapFieldErrors(errors);
  if (mapped[path]?.[0]) {
    return mapped[path][0];
  }
  const prefix = `${path}.`;
  const nested = Object.entries(mapped).find(([key]) => key.startsWith(prefix) || key.startsWith(`${path}[`));
  return nested?.[1]?.[0] || null;
}

export function collectFieldErrors(errors = {}, path) {
  const mapped = mapFieldErrors(errors);
  const messages = [...(mapped[path] || [])];
  Object.entries(mapped).forEach(([key, list]) => {
    if (key.startsWith(`${path}.`) || key.startsWith(`${path}[`)) {
      messages.push(...list);
    }
  });
  return messages;
}

function isUnsafeMessage(message) {
  const text = String(message || '');
  if (!text || text.length > 280) {
    return true;
  }
  return /stack trace|sqlstate|exception|Illuminate\\|vendor\/laravel|on line \d+|<\s*html/i.test(text);
}

export function humanizeApiError(error, t) {
  if (isTokenPersistenceError(error)) {
    return t('auth.storageUnavailable');
  }
  if (!(error instanceof ApiError)) {
    return t('errors.unexpected');
  }
  if (error.isNetworkError) {
    return t('errors.network');
  }
  if (error.isMalformed || error.status >= 500) {
    return t('errors.server');
  }
  if (error.isThrottled()) {
    if (error.retryAfterSeconds) {
      return t('errors.throttledWait', { seconds: error.retryAfterSeconds });
    }
    return isUnsafeMessage(error.message) ? t('errors.throttled') : (error.message || t('errors.throttled'));
  }
  if (error.isValidation()) {
    return isUnsafeMessage(error.message) ? t('errors.validation') : (error.message || t('errors.validation'));
  }
  if (error.isForbidden()) {
    return t('errors.forbidden');
  }
  if (error.isNotFound()) {
    return t('errors.notFound');
  }
  if (error.isConflict()) {
    return isUnsafeMessage(error.message) ? t('errors.conflict') : (error.message || t('errors.conflict'));
  }
  if (isUnsafeMessage(error.message)) {
    return t('errors.unexpected');
  }
  return error.message || t('errors.unexpected');
}
